import { StaffLoginBodySchema } from '@hadir/shared';
import { useEffect, useRef, useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { safeNext } from '@/lib/next-url';
import { LanguageToggle } from '@/components/layout/LanguageToggle';
import { Alert } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { FormField } from '@/components/ui/form-field';
import { Input } from '@/components/ui/input';
import { Spinner } from '@/components/ui/spinner';
import { useDocumentTitle } from '@/hooks/use-document-title';
import { ApiError } from '@/lib/api';
import { useErrorMessage } from '@/lib/error-message';
import { useAuthStore } from '@/stores/auth.store';

/**
 * Staff login — `/login` (05 §2.1).
 *
 * Two errors get first-class treatment because they are the two a TA actually
 * meets: `AUTH_INVALID_CREDENTIALS` (a typo) and `RATE_LIMITED` (5/min per
 * IP + email), where the button stays disabled and counts the `Retry-After`
 * window down rather than inviting a sixth failing attempt.
 */
export default function LoginPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const toMessage = useErrorMessage();
  const login = useAuthStore((state) => state.login);

  useDocumentTitle(t('auth.login.title'));

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [fieldErrors, setFieldErrors] = useState<{ email?: string; password?: string }>({});
  const [formError, setFormError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);
  const [retryAfter, setRetryAfter] = useState<number | null>(null);
  const emailRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    emailRef.current?.focus();
  }, []);

  // `RATE_LIMITED` countdown: one tick a second until the window is over.
  useEffect(() => {
    if (retryAfter === null) return;
    if (retryAfter <= 0) {
      setRetryAfter(null);
      setFormError(null);
      return;
    }
    const timer = setTimeout(
      () => setRetryAfter((value) => (value === null ? null : value - 1)),
      1000,
    );
    return () => clearTimeout(timer);
  }, [retryAfter]);

  function validate(): { email: string; password: string } | null {
    const errors: { email?: string; password?: string } = {};

    if (email.trim() === '') errors.email = t('validation.required');
    else if (!StaffLoginBodySchema.shape.email.safeParse(email).success) {
      errors.email = t('validation.email');
    }
    if (password === '') errors.password = t('validation.required');

    setFieldErrors(errors);
    if (errors.email || errors.password) return null;
    return { email: email.trim().toLowerCase(), password };
  }

  async function onSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (pending || retryAfter !== null) return;

    setFormError(null);
    const credentials = validate();
    if (!credentials) return;

    setPending(true);
    try {
      await login(credentials);
      navigate(safeNext(searchParams.get('next')), { replace: true });
    } catch (error) {
      if (error instanceof ApiError && error.code === 'RATE_LIMITED') {
        setRetryAfter(error.retryAfter ?? 60);
      }
      setFormError(toMessage(error));
    } finally {
      setPending(false);
    }
  }

  const blocked = pending || retryAfter !== null;

  return (
    <div className="grid min-h-screen place-items-center bg-background px-4 py-10">
      <div className="w-full max-w-sm">
        <div className="mb-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span
              aria-hidden="true"
              className="grid size-8 place-items-center rounded-md bg-primary text-primary-foreground"
            >
              <svg viewBox="0 0 32 32" className="size-5" fill="none" aria-hidden="true">
                <path
                  d="M9 8v16M23 8v16M9 16h14"
                  stroke="currentColor"
                  strokeWidth="3"
                  strokeLinecap="round"
                />
              </svg>
            </span>
            <span className="text-lg font-semibold">
              {t('app.name')} <span className="text-muted-foreground">· {t('app.nameLatin')}</span>
            </span>
          </div>
          <LanguageToggle />
        </div>

        <Card>
          <CardHeader>
            <CardTitle>{t('auth.login.title')}</CardTitle>
            <CardDescription>{t('auth.login.description')}</CardDescription>
          </CardHeader>
          <CardContent>
            <form className="space-y-4" onSubmit={onSubmit} noValidate>
              <FormField
                label={t('auth.login.email')}
                htmlFor="login-email"
                required
                {...(fieldErrors.email !== undefined ? { error: fieldErrors.email } : {})}
              >
                {({ id, describedBy, invalid }) => (
                  <Input
                    ref={emailRef}
                    id={id}
                    name="email"
                    type="email"
                    dir="ltr"
                    autoComplete="username"
                    inputMode="email"
                    data-testid="login-email"
                    aria-describedby={describedBy}
                    aria-invalid={invalid}
                    value={email}
                    onChange={(event) => setEmail(event.target.value)}
                    disabled={pending}
                  />
                )}
              </FormField>

              <FormField
                label={t('auth.login.password')}
                htmlFor="login-password"
                required
                {...(fieldErrors.password !== undefined ? { error: fieldErrors.password } : {})}
              >
                {({ id, describedBy, invalid }) => (
                  <Input
                    id={id}
                    name="password"
                    type="password"
                    dir="ltr"
                    autoComplete="current-password"
                    data-testid="login-password"
                    aria-describedby={describedBy}
                    aria-invalid={invalid}
                    value={password}
                    onChange={(event) => setPassword(event.target.value)}
                    disabled={pending}
                  />
                )}
              </FormField>

              {formError ? (
                <Alert variant="destructive" data-testid="login-error">
                  <p>{formError}</p>
                  {retryAfter !== null ? (
                    <p className="mt-1 text-xs tabular">
                      {t('auth.login.retryIn', { seconds: retryAfter })}
                    </p>
                  ) : null}
                </Alert>
              ) : null}

              <Button
                type="submit"
                className="w-full"
                disabled={blocked}
                data-testid="login-submit"
              >
                {pending ? <Spinner className="size-4" /> : null}
                {t('auth.login.submit')}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
